import { useState } from "react";
import { Eye, Gauge, Repeat2, ShieldCheck, Lightbulb } from "lucide-react";
import { FlowDiagram, FadeIn, Disclaimer, TechTag, slug } from "./common";
import { CategoryBackground } from "./SciBackground";
import CategoryHeader from "./CategoryHeader";
import CiCaseStudy from "./CiCaseStudy";
import { Slider } from "./ui/slider";

const PHILOSOPHY =
  "Digital tools are not the improvement themselves. They make manufacturing losses measurable, investigations faster and improvements easier to sustain.";

const LIFECYCLE = [
  { n: "01", t: "Observe", d: "Understand actual process performance as it is — not as it is assumed to be.", tools: ["Process Monitoring", "Control Charts", "GEMBA-style observation"] },
  { n: "02", t: "Define the Loss", d: "Quantify scrap, yield loss, retests, cycle time, recurring failure, variation and manual workload.", tools: ["Loss trees", "FPY / scrap metrics", "Workload logs"] },
  { n: "03", t: "Establish Baseline", d: "Determine the current state using reliable, verified data — the reference every improvement is judged against.", tools: ["Baseline datasets", "Data verification", "Trend snapshots"] },
  { n: "04", t: "Stratify", d: "Analyze by product, equipment, instrument, reagent, component lot, shift, time, process stage and initial vs retest.", tools: ["Stratified summaries", "Group comparisons"] },
  { n: "05", t: "Prioritize", d: "Use Pareto and risk to identify the highest-value opportunity — the vital few over the trivial many.", tools: ["Pareto", "FMEA", "Risk ranking"] },
  { n: "06", t: "Root Cause", d: "Use scientific RCA to determine why the loss occurs — converging evidence, not opinion.", tools: ["Fishbone", "5 Whys", "Hypothesis testing"] },
  { n: "07", t: "Improve", d: "Implement a technically justified process, Quality or digital change through controlled pathways.", tools: ["CAPA", "Change Control", "ECO"] },
  { n: "08", t: "Verify", d: "Measure whether performance actually improved against the baseline.", tools: ["Before/after comparison", "Statistical comparison"] },
  { n: "09", t: "Standardize", d: "Update controlled process documentation and workflows when appropriate so the gain survives personnel and time.", tools: ["SOP / WI updates", "Training", "Standard work"] },
  { n: "10", t: "Control", d: "Continue monitoring performance to ensure the gain remains sustained.", tools: ["Effectiveness checks", "Control charts", "Recurrence tracking"] },
];

const PDCA = [
  ["Plan", "Define the loss, baseline it, and form a testable improvement hypothesis."],
  ["Do", "Implement the change at a controlled, observable scale."],
  ["Check", "Compare performance against baseline — honestly, including null results."],
  ["Act", "Standardize what worked, correct what didn't, and set the next cycle."],
];
const DMAIC = [
  ["Define", "Bound the problem: product, stage, loss, magnitude, and why it matters."],
  ["Measure", "Establish a trustworthy baseline and confirm the measurement itself."],
  ["Analyze", "Stratify, compare distributions and test hypotheses until causes converge."],
  ["Improve", "Implement the technically justified, controlled change."],
  ["Control", "Monitor effectiveness so the improvement persists."],
];

const PROCESS_MAP = {
  current: ["Manual export from source systems", "Spreadsheet consolidation", "Manual data cleaning", "Hand-built Pareto", "Email-based review", "Findings re-assembled per investigation"],
  future: ["Automated acquisition on schedule", "Standardized, validated dataset", "Automated cleaning + exception log", "Interactive Pareto on demand", "Shared analytical layer", "Reusable investigation starting point"],
};

const KPI_LIBRARY = [
  { name: "First Pass Yield", why: "The cleanest measure of process health — every failure consumes capacity even when later recovered.", formula: { result: "FPY %", terms: [
    { label: "Passed on Initial Attempt", hint: "Runs accepted the first time, with no retest or rework." },
    { label: "÷ Total Initial Attempts", hint: "All first-time runs in the period — retests are excluded from the denominator." },
    { label: "× 100", hint: "Expressed as a percentage." },
  ] }, example: "Synthetic example: 861 of 1,000 initial attempts pass → 86.1%" },
  { name: "Final Yield", why: "What ultimately ships after recovery — the gap between FPY and final yield is the cost of retesting.", formula: { result: "Final Yield %", terms: [
    { label: "Final Accepted Units", hint: "Units accepted after any permitted retest or recovery." },
    { label: "÷ Total Units", hint: "All units entering the process." },
    { label: "× 100", hint: "Expressed as a percentage." },
  ] }, example: "Synthetic example: 918 of 1,000 units finally accepted → 91.8%" },
  { name: "Retest Rate", why: "Repeat evaluation consumes analyst time, instrument capacity and disposition speed.", formula: { result: "Retest Rate %", terms: [
    { label: "Units Requiring Repeat Evaluation", hint: "Runs that failed initially and entered retest." },
    { label: "÷ Total Units", hint: "All units in the period." },
    { label: "× 100", hint: "Expressed as a percentage." },
  ] }, example: "Synthetic example: 139 retests per 1,000 runs → 13.9%" },
  { name: "Scrap Rate", why: "The final, unrecovered loss — where Quality, yield and cost meet.", formula: { result: "Scrap Rate %", terms: [
    { label: "Scrapped Units", hint: "Units not recoverable after all permitted evaluation." },
    { label: "÷ Total Units", hint: "All units entering the process." },
    { label: "× 100", hint: "Expressed as a percentage." },
  ] }, example: "Synthetic example tracked as an index (baseline = 100) to avoid implying real costs." },
  { name: "Failure Recurrence", why: "A corrective action that doesn't prevent recurrence is a closure, not a fix.", example: "Track failure-mode frequency after CAPA implementation against its pre-CAPA baseline." },
  { name: "Process Capability (Cp / Cpk)", why: "Whether the process, as it actually behaves, fits inside the specification window.", formula: { result: "Cp", terms: [
    { label: "USL − LSL", hint: "Specification window width (synthetic demonstration limits)." },
    { label: "÷ 6σ", hint: "Six standard deviations — the natural spread of the process." },
  ] }, formula2: { result: "Cpk", terms: [
    { label: "min(USL − μ, μ − LSL)", hint: "Distance from the process mean to the nearest specification limit." },
    { label: "÷ 3σ", hint: "Three standard deviations — penalizes off-centre processes." },
  ] }, example: "Cp describes spread; Cpk adds centering. A capable-but-off-centre process has good Cp and poor Cpk." },
  { name: "Cycle Time", why: "How long the process actually takes — automation and loss removal both show up here.", example: "Measure door-to-door time per batch or per analytical run before and after workflow changes." },
  { name: "Investigation Lead Time", why: "From confirmed event to actionable output — where data automation pays back fastest.", example: "Automated acquisition and prepared datasets compress the hours spent assembling evidence." },
  { name: "Manual Hours Eliminated", why: "Repetitive expert time returned to actual investigation and improvement.", example: "Estimated from task frequency × manual duration before automation, clearly labelled as an estimate." },
];

function Formula({ formula }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="mt-3" data-testid={`formula-${slug(formula.result)}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {formula.terms.map((t, i) => (
          <button
            key={t.label}
            data-testid={`formula-term-${slug(formula.result)}-${i}`}
            onClick={() => setOpen(open === i ? null : i)}
            className={`font-mono text-[11px] px-2.5 py-1.5 rounded-sm border transition-colors ${
              open === i ? "border-cyan bg-cyan/15 text-paper" : "border-teal/40 text-pale/80 hover:border-cyan/60"
            }`}
          >
            {t.label}
          </button>
        ))}
        <span className="font-mono text-[11px] text-cyan px-1">=</span>
        <span className="font-mono text-[11px] px-2.5 py-1.5 rounded-sm bg-teal/20 border border-cyan/50 text-cyan">{formula.result}</span>
      </div>
      <p className="text-[11px] text-steel mt-2 min-h-[16px]">
        {open !== null && formula.terms[open] ? formula.terms[open].hint : "Select a term to see what it represents."}
      </p>
    </div>
  );
}

function FmeaDemo() {
  const [s, setS] = useState(7);
  const [o, setO] = useState(4);
  const [d, setD] = useState(5);
  const rpn = s * o * d;
  const band = rpn >= 150 ? ["High — prioritize mitigation", "text-critical"] : rpn >= 60 ? ["Moderate — monitor and improve", "text-warn"] : ["Low — accept with controls", "text-success"];
  return (
    <div data-testid="fmea-demo">
      <div className="grid sm:grid-cols-3 gap-4 mt-3">
        {[["Severity", s, setS, "How serious is the effect?"], ["Occurrence", o, setO, "How often does the cause appear?"], ["Detectability", d, setD, "How likely is it to escape detection? (higher = harder to detect)"]].map(([label, val, setter, hint]) => (
          <div key={label}>
            <div className="flex justify-between">
              <p className="font-mono text-[10px] uppercase tracking-wider text-steel">{label}</p>
              <p className="font-mono text-xs text-cyan" data-testid={`fmea-${label.toLowerCase()}-value`}>{val}</p>
            </div>
            <Slider value={[val]} min={1} max={10} step={1} onValueChange={(v) => setter(v[0])} className="mt-2" data-testid={`fmea-slider-${label.toLowerCase()}`} />
            <p className="text-[10px] text-steel mt-1">{hint}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-4">
        <p className="font-mono text-sm text-pale/70">Risk priority: S × O × D =</p>
        <p className="font-mono text-2xl text-cyan" data-testid="fmea-rpn">{rpn}</p>
        <p className={`font-mono text-xs ${band[1]}`} data-testid="fmea-band">{band[0]}</p>
      </div>
      <p className="text-[10px] text-steel mt-2">Concept demonstration only — no proprietary employer FMEA content.</p>
    </div>
  );
}

export default function ContinuousImprovement() {
  const [stage, setStage] = useState(0);
  const [framework, setFramework] = useState("PDCA");
  const [tool, setTool] = useState("Pareto");
  const [metric, setMetric] = useState(KPI_LIBRARY[0]);

  const OPEX = [
    { icon: Eye, t: "Visibility", d: "Make process performance measurable — losses you can see are losses you can attack." },
    { icon: Gauge, t: "Speed", d: "Reduce the time required to collect and prepare investigation data." },
    { icon: Repeat2, t: "Consistency", d: "Standardize analytical workflows so every investigation starts from the same trusted base." },
    { icon: ShieldCheck, t: "Sustainability", d: "Monitor whether improvements remain effective after implementation." },
  ];

  const QUALITY_FLOW = [
    { title: "Regulated Improvement Pathway", items: [
      { label: "Opportunity", detail: "A prioritized, quantified loss worth solving." },
      { label: "Evidence", detail: "Baseline data, stratification and verified measurement." },
      { label: "Risk Assessment", detail: "What could the change affect? FMEA-style thinking before action." },
      { label: "RCA", detail: "Understand why before deciding what." },
      { label: "Proposed Improvement", detail: "A technically justified change with expected effect." },
      { label: "Quality Review", detail: "Quality Systems evaluates impact on the validated/controlled state." },
      { label: "Change Control", detail: "Approval, documentation and traceability — the change becomes official." },
      { label: "Implementation", detail: "Executed with manufacturing communication and training where applicable." },
      { label: "Verification", detail: "Did performance actually move against baseline?" },
      { label: "Effectiveness Check", detail: "Does it stay moved? Monitored over an appropriate period." },
    ]},
  ];

  const DIGITAL_FLOW = [
    { title: "Digital Continuous Improvement Workflow", items: [
      { label: "Manufacturing Problem", detail: "A loss, excursion or recurring failure in the real process." },
      { label: "Measure", detail: "Define what trustworthy evidence would look like." },
      { label: "Automate Data Collection", detail: "Harvesters and APIs replace manual retrieval." },
      { label: "Validate Data", detail: "Schema, deduplication, traceability — before anyone analyzes." },
      { label: "Analyze", detail: "Pareto, distributions, trends, stratification." },
      { label: "Identify Loss Driver", detail: "The vital few causes, evidenced." },
      { label: "Improve Process", detail: "Controlled change through Quality pathways." },
      { label: "Automate Monitoring", detail: "Dashboards and scheduled checks watch the metric that mattered." },
      { label: "Sustain Improvement", detail: "Effectiveness confirmed over time — the loop closes." },
    ]},
  ];

  return (
    <section id="continuous-improvement" className="relative overflow-hidden scroll-mt-16" data-testid="continuous-improvement-section">
      <CategoryBackground variant="pdca" accent="success" accent2="teal" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <CategoryHeader
          id="continuous-improvement"
          category="continuous-improvement"
          eyebrow="Continuous Improvement & Operational Excellence"
          title="From evidence to sustained performance"
          description="PDCA/DMAIC-style improvement with KPI governance — using manufacturing data, scientific investigation and digital tools to identify losses, reduce variation and sustain measurable process improvements."
        />

        <FadeIn>
          <div className="panel p-6 lg:p-8 mt-10 border-l-2 border-l-cyan sci-grid" data-testid="ci-philosophy">
            <div className="flex items-start gap-4">
              <Lightbulb className="w-6 h-6 text-cyan shrink-0 mt-1" aria-hidden="true" />
              <p className="font-sans text-lg md:text-xl text-paper leading-relaxed max-w-4xl">{PHILOSOPHY}</p>
            </div>
          </div>
        </FadeIn>

        {/* Lifecycle */}
        <div className="mt-16">
          <h3 className="font-sans tracking-tight text-2xl text-paper">The Improvement Lifecycle</h3>
          <p className="text-steel text-sm mt-2">Ten stages, each clickable. Problem → data → RCA → improvement → control → sustainment.</p>
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6 mt-8">
            <div className="space-y-1" data-testid="ci-lifecycle">
              {LIFECYCLE.map((s, i) => (
                <div key={s.n}>
                  <button
                    data-testid={`ci-stage-${s.n}`}
                    onClick={() => setStage(i)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-sm border text-left transition-colors ${
                      stage === i ? "border-cyan bg-cyan/10" : "border-transparent hover:border-teal/40"
                    }`}
                  >
                    <span className={`font-mono text-xs w-6 ${stage === i ? "text-cyan" : "text-steel"}`}>{s.n}</span>
                    <span className={`font-sans text-sm font-semibold uppercase tracking-wide ${stage === i ? "text-paper" : "text-pale/70"}`}>{s.t}</span>
                  </button>
                  {i < LIFECYCLE.length - 1 && <div className="ml-7 h-2.5 w-px bg-teal/40" aria-hidden="true" />}
                </div>
              ))}
            </div>
            <div className="panel p-6 self-start lg:sticky lg:top-20" data-testid="ci-stage-detail">
              <p className="font-mono text-cyan text-xs">{LIFECYCLE[stage].n}</p>
              <h4 className="font-sans text-xl font-semibold text-paper mt-2 uppercase tracking-wide">{LIFECYCLE[stage].t}</h4>
              <p className="text-sm text-pale/85 mt-3 leading-relaxed">{LIFECYCLE[stage].d}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel mt-5 mb-2">Relevant tools</p>
              <div className="flex flex-wrap gap-1.5">
                {LIFECYCLE[stage].tools.map((t) => (
                  <TechTag key={t}>{t}</TechTag>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Toolbox */}
        <div className="mt-20">
          <h3 className="font-sans tracking-tight text-2xl text-paper">Continuous Improvement Toolbox</h3>
          <p className="text-steel text-sm mt-2 max-w-3xl">
            Practical application of Lean / Six Sigma-style problem-solving principles in regulated manufacturing — methodology, not certification claims.
          </p>
          <div className="flex flex-wrap gap-2 mt-6" data-testid="ci-toolbox-tabs">
            {["Pareto", "Process Mapping", "5 Whys", "Fishbone", "FMEA", "Statistical Analysis", "Effectiveness Checks"].map((t) => (
              <button
                key={t}
                data-testid={`ci-tool-${slug(t)}`}
                onClick={() => setTool(t)}
                className={`font-mono text-[11px] px-3 py-1.5 rounded-sm border transition-colors ${
                  tool === t ? "bg-cyan text-navy border-cyan" : "border-teal/40 text-pale/70 hover:border-cyan/60 hover:text-paper"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="panel p-6 mt-4" data-testid="ci-tool-detail">
            {tool === "Pareto" && (
              <>
                <h4 className="font-sans text-lg font-semibold text-paper">Pareto Analysis</h4>
                <p className="text-sm text-pale/80 mt-2 leading-relaxed">
                  Identifies the small number of causes driving the majority of losses. In the case study below, one failure mode — High Migration Time —
                  carries the programme's attention because the Pareto says so, not because it is loudest in the hallway.
                </p>
              </>
            )}
            {tool === "Process Mapping" && (
              <>
                <h4 className="font-sans text-lg font-semibold text-paper">Process Mapping — Current vs Future State</h4>
                <p className="text-sm text-pale/80 mt-2">Expose delays, redundant steps, manual handoffs, duplicate data entry and non-value-added work.</p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  {[["Current State", PROCESS_MAP.current, "text-warn"], ["Future State", PROCESS_MAP.future, "text-success"]].map(([label, steps, tone]) => (
                    <div key={label} className="panel p-4 bg-navy" data-testid={`process-map-${slug(label)}`}>
                      <p className={`font-mono text-[10px] uppercase tracking-[0.2em] ${tone}`}>{label}</p>
                      <ol className="mt-3 space-y-2">
                        {steps.map((s2, i) => (
                          <li key={s2} className="flex items-start gap-2 text-xs text-pale/80">
                            <span className="font-mono text-teal w-4 shrink-0">{i + 1}.</span> {s2}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              </>
            )}
            {tool === "5 Whys" && (
              <>
                <h4 className="font-sans text-lg font-semibold text-paper">5 Whys</h4>
                <p className="text-sm text-pale/80 mt-2 leading-relaxed">
                  Follows a causal chain past the symptom: "yield dropped" → "migration-time failures grew" → "the population shifted" → "the shift
                  predates the current criteria" → "specification and process drifted out of alignment". Each 'why' must survive evidence; the chain stops
                  where data stops.
                </p>
              </>
            )}
            {tool === "Fishbone" && (
              <>
                <h4 className="font-sans text-lg font-semibold text-paper">Fishbone / Ishikawa</h4>
                <p className="text-sm text-pale/80 mt-2 leading-relaxed">
                  Structures hypotheses across Machine, Material, Method, Measurement, Environment and People before any is tested. An interactive
                  fishbone lives in the RCA Lab toolbox above.
                </p>
              </>
            )}
            {tool === "FMEA" && (
              <>
                <h4 className="font-sans text-lg font-semibold text-paper">FMEA — Risk-Based Assessment</h4>
                <p className="text-sm text-pale/80 mt-2">Severity, occurrence and detectability combine into risk prioritization. Try the concept:</p>
                <FmeaDemo />
              </>
            )}
            {tool === "Statistical Analysis" && (
              <>
                <h4 className="font-sans text-lg font-semibold text-paper">Statistical Process Analysis</h4>
                <p className="text-sm text-pale/80 mt-2 leading-relaxed">
                  Mean and standard deviation describe the population; distributions and 3σ concepts separate natural variation from signals; trend
                  analysis catches directional movement; Cp/Cpk assess the process against its specification window. Variation analysis answers "is this
                  new, or just noise?" before resources move.
                </p>
              </>
            )}
            {tool === "Effectiveness Checks" && (
              <>
                <h4 className="font-sans text-lg font-semibold text-paper">Effectiveness Checks</h4>
                <p className="text-sm text-pale/80 mt-2 leading-relaxed">
                  A completed CAPA or implemented change is not sufficient by itself. Performance must be checked after implementation: recurrence, FPY,
                  scrap, process variation, retest rate, defects and cycle time — over a period long enough to mean something.
                </p>
              </>
            )}
          </div>

          {/* PDCA / DMAIC frameworks */}
          <div className="panel p-6 mt-4" data-testid="ci-frameworks">
            <div className="flex items-center gap-2 mb-4">
              {["PDCA", "DMAIC"].map((f) => (
                <button
                  key={f}
                  data-testid={`ci-framework-${f.toLowerCase()}`}
                  onClick={() => setFramework(f)}
                  className={`font-mono text-[11px] px-3 py-1.5 rounded-sm border transition-colors ${
                    framework === f ? "bg-cyan text-navy border-cyan" : "border-teal/40 text-pale/70 hover:border-cyan/60"
                  }`}
                >
                  {f}
                </button>
              ))}
              <p className="text-xs text-steel ml-2">
                {framework === "PDCA" ? "The iterative improvement engine — useful whenever a change needs a learning loop." : "A structured improvement framework for defined, measurable losses."}
              </p>
            </div>
            <div className={`grid gap-3 ${framework === "PDCA" ? "sm:grid-cols-4" : "sm:grid-cols-5"}`}>
              {(framework === "PDCA" ? PDCA : DMAIC).map(([t, d], i) => (
                <div key={t} className="panel p-4 bg-navy" data-testid={`ci-framework-step-${t.toLowerCase()}`}>
                  <p className="font-mono text-[10px] text-teal">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-sans text-sm font-semibold text-cyan mt-1">{t}</p>
                  <p className="text-[11px] text-steel mt-2 leading-snug">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Flagship case study */}
        <CiCaseStudy />

        {/* Digital CI */}
        <div className="mt-20" data-testid="digital-ci">
          <h3 className="font-sans tracking-tight text-2xl text-paper">Digital Continuous Improvement</h3>
          <p className="text-steel text-sm mt-2 max-w-3xl">
            Digital tools compress the time between problem occurrence and reliable manufacturing insight. Examples of the digital process-improvement
            approach demonstrated through the automation work on this site.
          </p>
          <div className="grid lg:grid-cols-2 gap-6 mt-8 items-start">
            <FlowDiagram layers={DIGITAL_FLOW} testId="digital-ci-flow" />
            <div className="grid gap-4">
              <div className="panel p-5" data-testid="digital-ci-before">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-warn">Traditional / Manual State</p>
                <ul className="mt-3 space-y-1.5">
                  {["Multiple spreadsheets", "Manual exports", "Manual data cleaning", "Manual Pareto", "Repeated analyst effort", "Slow investigations", "Difficult historical comparison", "Limited automated monitoring"].map((x) => (
                    <li key={x} className="text-xs text-pale/70 font-body">— {x}</li>
                  ))}
                </ul>
              </div>
              <div className="panel p-5 border-l-2 border-l-success" data-testid="digital-ci-after">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-success">Improved Digital State</p>
                <ul className="mt-3 space-y-1.5">
                  {["Automated acquisition", "Standardized data", "Automated cleaning", "Interactive Pareto", "Reusable analytics", "Faster RCA support", "Historical trending", "Continuous monitoring"].map((x) => (
                    <li key={x} className="text-xs text-pale/85 font-body">— {x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* KPI library */}
        <div className="mt-20" data-testid="ci-kpi-library">
          <h3 className="font-sans tracking-tight text-2xl text-paper">Manufacturing Improvement Metrics</h3>
          <p className="text-steel text-sm mt-2">The measurement vocabulary of improvement. Select a metric; hover or tap formula terms to inspect them.</p>
          <div className="grid lg:grid-cols-[260px_1fr] gap-6 mt-8">
            <div className="grid gap-1.5 content-start">
              {KPI_LIBRARY.map((k) => (
                <button
                  key={k.name}
                  data-testid={`ci-kpi-${slug(k.name)}`}
                  onClick={() => setMetric(k)}
                  className={`text-left px-3 py-2.5 rounded-sm border transition-colors ${
                    metric.name === k.name ? "border-cyan bg-cyan/10" : "border-teal/30 bg-navy2 hover:border-cyan/50"
                  }`}
                >
                  <p className={`font-sans text-sm font-semibold ${metric.name === k.name ? "text-cyan" : "text-paper"}`}>{k.name}</p>
                </button>
              ))}
            </div>
            <div className="panel p-6" data-testid="ci-kpi-detail">
              <h4 className="font-sans text-xl font-semibold text-paper">{metric.name}</h4>
              <p className="text-sm text-pale/85 mt-2 leading-relaxed">{metric.why}</p>
              {metric.formula && <Formula key={`${metric.name}-primary`} formula={metric.formula} />}
              {metric.formula2 && <Formula key={`${metric.name}-secondary`} formula={metric.formula2} />}
              <div className="mt-3"><Disclaimer>{metric.example}</Disclaimer></div>
            </div>
          </div>
        </div>

        {/* OpEx cards */}
        <div className="mt-20">
          <h3 className="font-sans tracking-tight text-2xl text-paper">Operational Excellence Through Better Information</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
            {OPEX.map((o, i) => {
              const Icon = o.icon;
              return (
                <FadeIn key={o.t} delay={i * 0.06}>
                  <div className="panel p-6 h-full hover:border-cyan/40 transition-colors" data-testid={`opex-${o.t.toLowerCase()}`}>
                    <Icon className="w-5 h-5 text-cyan" aria-hidden="true" />
                    <h4 className="font-sans text-base font-semibold text-paper mt-4 uppercase tracking-wide">{o.t}</h4>
                    <p className="text-xs text-steel mt-2 leading-relaxed">{o.d}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>

        {/* CI + Quality */}
        <div className="mt-20 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-start">
          <div>
            <h3 className="font-sans tracking-tight text-2xl text-paper">Continuous Improvement Inside Quality Systems</h3>
            <p className="text-steel text-sm mt-2 mb-6 max-w-xl">
              In regulated manufacturing, improvement cannot bypass Quality controls. The pathway below is how a good idea becomes a controlled, verified,
              sustained change.
            </p>
            <FlowDiagram layers={QUALITY_FLOW} testId="ci-quality-flow" />
          </div>
          <div className="panel p-6 border-l-2 border-l-cyan lg:sticky lg:top-20" data-testid="ci-quality-principle">
            <p className="overline-label">Operating Principle</p>
            <p className="font-sans text-xl text-paper mt-3 leading-relaxed">Improve the process without losing control of the process.</p>
            <p className="text-sm text-steel mt-4 leading-relaxed">
              Every improvement on this site travels through evidence, risk assessment, RCA, Quality review and change control — then stays under
              effectiveness monitoring. Speed comes from better data and automation, never from skipping control.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
